import type { BBox, FeatureCollection, Geometry, GeometryObject } from 'geojson';
import type { LngLatBoundsLike, PointLike, Map as MapRef } from 'mapbox-gl';

/**
 * `[west, south, east, north]` — the array form mapbox-gl accepts as a
 * `LngLatBoundsLike`. Used instead of a `LngLatBounds` instance so that
 * framework-independent domain code does not have to import mapbox-gl at
 * runtime (see docs/performance/meta-app-bundle-size-audit-2026-09.md §5 #3).
 */
export type BdMapBoundsTuple = [number, number, number, number];

/**
 * Everything {@link bdToMapBoundsTuple} normalizes. Wider than mapbox-gl's own
 * `LngLatBoundsLike`, which since v3 no longer lists the `{ sw, ne }` object
 * form — we keep accepting it because callers persist bounds in that shape.
 */
export type BdMapBoundsLike = LngLatBoundsLike | { sw: unknown; ne: unknown };

type BdLngLatTuple = [number, number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Normalizes mapbox-gl's `LngLatLike` shapes to `[lng, lat]`. */
function toLngLatTuple(value: unknown): BdLngLatTuple {
  if (Array.isArray(value) && value.length >= 2) {
    const [lng, lat] = value;

    if (typeof lng === 'number' && typeof lat === 'number') {
      return [lng, lat];
    }
  }

  if (isRecord(value)) {
    const lng = typeof value['lng'] === 'number' ? value['lng'] : value['lon'];
    const lat = value['lat'];

    if (typeof lng === 'number' && typeof lat === 'number') {
      return [lng, lat];
    }
  }

  throw new Error('bounds corner is not a LngLatLike');
}

function cornerOf(
  value: unknown,
  getter: string,
  key: string,
  legacyKey: string,
): unknown {
  if (!isRecord(value)) {
    return undefined;
  }

  const get = value[getter];

  if (typeof get === 'function') {
    return (get as () => unknown).call(value);
  }

  return value[key] ?? value[legacyKey];
}

/**
 * Normalizes every `LngLatBoundsLike` shape mapbox-gl's `LngLatBounds.convert`
 * accepts — `[w, s, e, n]`, `[[w, s], [e, n]]`, `{ sw, ne }` and a
 * `LngLatBounds` instance (`getSouthWest()`/`getNorthEast()` or the internal
 * `_sw`/`_ne`) — to `[west, south, east, north]`.
 */
export function bdToMapBoundsTuple(bounds: BdMapBoundsLike): BdMapBoundsTuple {
  if (Array.isArray(bounds)) {
    if (bounds.length === 4 && bounds.every((part) => typeof part === 'number')) {
      const [west, south, east, north] = bounds as BdMapBoundsTuple;

      return [west, south, east, north];
    }

    if (bounds.length === 2) {
      const [west, south] = toLngLatTuple(bounds[0]);
      const [east, north] = toLngLatTuple(bounds[1]);

      return [west, south, east, north];
    }

    throw new Error('bounds array has neither 2 nor 4 entries');
  }

  const southWest = cornerOf(bounds, 'getSouthWest', 'sw', '_sw');
  const northEast = cornerOf(bounds, 'getNorthEast', 'ne', '_ne');

  if (southWest !== undefined && northEast !== undefined) {
    const [west, south] = toLngLatTuple(southWest);
    const [east, north] = toLngLatTuple(northEast);

    return [west, south, east, north];
  }

  throw new Error('bounds is not a LngLatBoundsLike');
}

export class BdMapBounds<T extends Geometry = GeometryObject> {
  public static fromBbox<T extends Geometry = GeometryObject>(
    bbox: BBox,
    origin: FeatureCollection<T>,
  ): BdMapBounds<T> {
    if (!Array.isArray(bbox)) {
      throw new Error('bbox is not an array');
    }

    if (bbox.length === 6) {
      return new BdMapBounds(
        bdToMapBoundsTuple(bbox.slice(0, 4) as BdMapBoundsTuple),
        origin,
      );
    }

    return new BdMapBounds<T>(bdToMapBoundsTuple(bbox as BdMapBoundsTuple), origin);
  }

  public static fromBoundsLike<T extends Geometry>(
    bounds: LngLatBoundsLike,
    origin: FeatureCollection<T>,
  ): BdMapBounds<T> {
    return new BdMapBounds<T>(bdToMapBoundsTuple(bounds), origin);
  }

  protected constructor(
    public bounds: BdMapBoundsTuple,
    public origin: FeatureCollection<T>,
  ) {}

  public toPointLikeArray(mapRef: MapRef): [PointLike, PointLike] {
    const [west, south, east, north] = this.bounds;

    return [mapRef.project([west, south]), mapRef.project([east, north])];
  }
}
